import {useRef, useState} from "react";
import styled from "styled-components";
import {createGuestInquiry, guestErrorCode, guestErrorMessage} from "api/guest.api";
import {getAnonId, getAttribution, getMetaIds, metaTrack, track} from "analytics";
import {TURNSTILE_SITE_KEY} from "conts/features";
import {Turnstile} from "ui/base/turnstile/Turnstile";
import {
    Alert,
    AlertIcon,
    Avatar,
    Field,
    FieldError,
    Input,
    Label,
    LabelHint,
    LockIcon,
    Muted,
    PillButton,
    SendIcon,
    Spinner,
    TextArea,
    tokens,
} from "ui/blocks/guestMessaging/GuestUI";

const MESSAGE_MAX = 2000;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const newKey = () =>
    window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;

/**
 * Guest message form: message, full name and email. Calls onSent only when
 * the backend confirms the message was delivered.
 */
export const GuestMessageForm = ({
    teacherUserId,
    instructorFirstName,
    instructorPhoto,
    subject,
    initialMessage = "",
    onSent,
    onLoginRequired,
}) => {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [message, setMessage] = useState(initialMessage);
    const [website, setWebsite] = useState(""); // honeypot
    const [errors, setErrors] = useState({});
    const [formError, setFormError] = useState("");
    const [accountExists, setAccountExists] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [turnstileToken, setTurnstileToken] = useState("");
    const [turnstileReset, setTurnstileReset] = useState(0);

    const startedRef = useRef(false);
    // One key per message: a retry after a network error can't send it twice.
    const idempotencyKeyRef = useRef(newKey());
    const instructor = instructorFirstName || "the instructor";

    const markStarted = () => {
        if (startedRef.current) return;
        startedRef.current = true;
        track("message_form_started", {teacher_user_id: teacherUserId, guest: true});
    };

    const change = (setter, field) => (e) => {
        markStarted();
        setter(e.target.value);
        if (errors[field]) setErrors((prev) => ({...prev, [field]: ""}));
        setFormError("");
        setAccountExists(false);
    };

    const validate = () => {
        const next = {};
        if (!message.trim()) next.message = "Write a short message to the instructor.";
        else if (message.trim().length > MESSAGE_MAX) next.message = `Keep it under ${MESSAGE_MAX} characters.`;
        if (!name.trim()) next.name = "Enter your name.";
        if (!EMAIL_RE.test(email.trim())) next.email = "Enter a valid email address.";
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const needsBotCheck = Boolean(TURNSTILE_SITE_KEY);
    const waitingForBotCheck = needsBotCheck && !turnstileToken;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isLoading || !validate() || waitingForBotCheck) return;

        track("message_submitted", {teacher_user_id: teacherUserId, guest: true, length: message.trim().length});
        metaTrack("Contact", {content_category: "guest_message"});

        setIsLoading(true);
        setFormError("");
        try {
            const result = await createGuestInquiry({
                teacher_user_id: Number(teacherUserId),
                name: name.trim(),
                email: email.trim(),
                message: message.trim(),
                turnstile_token: turnstileToken,
                website,
                idempotency_key: idempotencyKeyRef.current,
                anon_id: getAnonId(),
                attribution: getAttribution(),
                source_url: window.location.href.slice(0, 500),
                ...getMetaIds(),
            });

            // Same event id as the server-side Conversions API Lead, so Meta
            // counts it once.
            metaTrack("Lead", {content_category: "guest_message"}, result?.event_id);
            idempotencyKeyRef.current = newKey();
            onSent({instructorName: result?.instructor_name || instructorFirstName, email: email.trim()});
        } catch (error) {
            const code = guestErrorCode(error);
            track("message_send_failed", {teacher_user_id: teacherUserId, guest: true, error: code || "network"});
            if (code === "account_exists") {
                setAccountExists(true);
            } else {
                setFormError(guestErrorMessage(error, "We couldn't send your message. Check your connection and try again."));
            }
            // Turnstile tokens are single-use.
            setTurnstileReset((n) => n + 1);
        } finally {
            setIsLoading(false);
        }
    };

    const remaining = MESSAGE_MAX - message.length;

    return (
        <Form onSubmit={handleSubmit} noValidate aria-busy={isLoading}>
            <Header>
                <Avatar src={instructorPhoto} name={instructor} size={56}/>
                <div>
                    <Title>Message {instructor}</Title>
                    {subject && <Subtitle>{subject}</Subtitle>}
                </div>
            </Header>

            <Field>
                <Label htmlFor="guest-message">
                    Your message
                    {remaining < 200 && <LabelHint $warn={remaining < 0}>{remaining} left</LabelHint>}
                </Label>
                <TextArea
                    id="guest-message"
                    placeholder={`Hi ${instructor}, I'm interested in lessons. I'm a beginner and I'm free on weekends.`}
                    value={message}
                    onChange={change(setMessage, "message")}
                    aria-invalid={Boolean(errors.message)}
                    aria-describedby={errors.message ? "guest-message-error" : undefined}
                    $minHeight={136}
                />
                {errors.message && <FieldError id="guest-message-error">{errors.message}</FieldError>}
            </Field>

            <Row>
                <Field>
                    <Label htmlFor="guest-name">Full name</Label>
                    <Input
                        id="guest-name"
                        autoComplete="name"
                        maxLength={80}
                        value={name}
                        onChange={change(setName, "name")}
                        aria-invalid={Boolean(errors.name)}
                        aria-describedby={errors.name ? "guest-name-error" : undefined}
                    />
                    {errors.name && <FieldError id="guest-name-error">{errors.name}</FieldError>}
                </Field>
                <Field>
                    <Label htmlFor="guest-email">Email</Label>
                    <Input
                        id="guest-email"
                        type="email"
                        autoComplete="email"
                        inputMode="email"
                        maxLength={254}
                        value={email}
                        onChange={change(setEmail, "email")}
                        aria-invalid={Boolean(errors.email)}
                        aria-describedby={errors.email ? "guest-email-error" : undefined}
                    />
                    {errors.email && <FieldError id="guest-email-error">{errors.email}</FieldError>}
                </Field>
            </Row>

            {/* Honeypot: hidden from people, often filled by bots. */}
            <Honeypot aria-hidden="true">
                <label htmlFor="guest-website">Website</label>
                <input id="guest-website" tabIndex={-1} autoComplete="off" value={website}
                       onChange={(e) => setWebsite(e.target.value)}/>
            </Honeypot>

            {needsBotCheck && (
                <Turnstile siteKey={TURNSTILE_SITE_KEY} onToken={setTurnstileToken} resetSignal={turnstileReset}/>
            )}

            {accountExists && (
                <Alert $tone="warning" role="alert">
                    <AlertIcon/>
                    <span>
                        This email already has a Teache account. Log in to send your message — we'll keep what you
                        wrote. Or use a different email.
                    </span>
                </Alert>
            )}
            {formError && (
                <Alert role="alert">
                    <AlertIcon/>
                    <span>{formError}</span>
                </Alert>
            )}

            {accountExists ? (
                <PillButton type="button" $block onClick={() => onLoginRequired(message)}>
                    Log in to send
                </PillButton>
            ) : (
            <PillButton type="submit" $block disabled={isLoading || waitingForBotCheck}>
                {isLoading ? (
                    <>
                        <Spinner $light $size={16}/> Sending…
                    </>
                ) : (
                    <>
                        <SendIcon size={16}/> Send message
                    </>
                )}
            </PillButton>
            )}

            <Muted>
                <LockIcon size={14}/>
                <span>Your email stays private. We only use it to let you know when {instructor} replies.</span>
            </Muted>
        </Form>
    );
};

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: 18px;
    width: 100%;
    box-sizing: border-box;
    font-family: var(--font-primary);
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
    padding-right: 32px; /* room for the modal's close button */
`;

const Title = styled.h2`
    margin: 0;
    font-size: 22px;
    font-weight: 600;
    line-height: 1.25;
    color: ${tokens.ink};
`;

const Subtitle = styled.p`
    margin: 2px 0 0;
    font-size: 14px;
    color: ${tokens.muted};
`;

const Row = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 18px;

    @media (min-width: 560px) {
        grid-template-columns: 1fr 1fr;
        gap: 14px;
    }
`;

const Honeypot = styled.div`
    position: absolute;
    left: -10000px;
    width: 1px;
    height: 1px;
    overflow: hidden;
`;
